import React, { useState } from 'react';
import { X, Loader } from 'lucide-react';
import { api } from '../../services/api';

/**
 * Download Files Modal — shared popup for downloading document files.
 *
 * Props:
 *  - documents: array of { id, title } — the documents available for download
 *  - envelopeTitle: string — the overall envelope/bundle title (for combined filename)
 *  - bundleId: string|null — if this is a bundle, the bundle ID
 *  - documentId: string|null — if this is a single document (no bundle), the document ID
 *  - onClose: () => void
 */
const DownloadFilesModal = ({ documents = [], envelopeTitle, bundleId, documentId, onClose }) => {
  // State: which doc indexes are selected, plus certificate
  const [selectedDocs, setSelectedDocs] = useState(() => documents.map((_, i) => i));
  const [downloadCert, setDownloadCert] = useState(true);
  const [combineAll, setCombineAll] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const totalFileCount = selectedDocs.length + (downloadCert ? 1 : 0);
  const maxFileCount = documents.length + 1; // all docs + certificate
  const allChecked = selectedDocs.length === documents.length && downloadCert;

  const toggleDocFile = (idx) => {
    if (combineAll) return;
    setSelectedDocs(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const toggleAll = () => {
    if (combineAll) return;
    if (allChecked) {
      setSelectedDocs([]);
      setDownloadCert(false);
    } else {
      setSelectedDocs(documents.map((_, i) => i));
      setDownloadCert(true);
    }
  };

  const handleCombineToggle = () => {
    const next = !combineAll;
    setCombineAll(next);
    if (next) {
      setSelectedDocs(documents.map((_, i) => i));
      setDownloadCert(true);
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    if (totalFileCount === 0) return;
    setDownloading(true);
    const title = envelopeTitle || documents[0]?.title || 'document';
    const safeTitle = title.replace(/[^a-zA-Z0-9]/g, '_');

    try {
      if (bundleId) {
        // ─── Bundle download (uses backend bundle API) ───
        const options = {
          document_ids: selectedDocs.map(idx => documents[idx]?.id).filter(Boolean),
          include_certificate: downloadCert,
          combine: combineAll,
        };

        const blob = await api.downloadBundleFiles(bundleId, options);
        const ext = combineAll ? 'pdf' : (totalFileCount > 1 && !combineAll ? 'zip' : 'pdf');
        const suffix = combineAll ? '_combined' : (totalFileCount > 1 ? '_documents' : '_signed');
        triggerDownload(blob, `${safeTitle}${suffix}.${ext}`);

      } else if (documentId) {
        // ─── Single document download ───
        const options = {
          include_document: selectedDocs.length > 0,
          include_certificate: downloadCert,
          combine: combineAll,
        };

        const blob = await api.downloadDocumentFiles(documentId, options);
        const ext = combineAll ? 'pdf' : (totalFileCount > 1 && !combineAll ? 'zip' : 'pdf');
        const suffix = combineAll ? '_combined' : (totalFileCount > 1 ? '_documents' : '_signed');
        triggerDownload(blob, `${safeTitle}${suffix}.${ext}`);

      } else {
        // ─── Legacy fallback: download files individually ───
        for (const idx of selectedDocs) {
          const doc = documents[idx];
          const blob = await api.downloadDocument(doc.id);
          triggerDownload(blob, (doc.title || 'document') + '_signed.pdf');
        }
        if (downloadCert) {
          const blob = await api.downloadCertificate(documents[0].id);
          triggerDownload(blob, title + '_certificate.pdf');
        }
      }

      onClose();
    } catch (err) {
      alert('Download failed: ' + (err.message || 'Unknown error'));
    } finally {
      setDownloading(false);
    }
  };

  const individualDisabled = combineAll;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="px-6 py-5 flex justify-between items-start">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Download files</h3>
            <p className="text-sm text-gray-500 mt-1">Select which files you want to download:</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 -mr-1 -mt-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* File options */}
        <div className="px-6 pb-4 space-y-0">
          {/* All */}
          <label className={`flex items-start py-3 border-b border-gray-100 ${individualDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={allChecked}
              onChange={toggleAll}
              disabled={individualDisabled}
              className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-5 w-5 disabled:opacity-50"
            />
            <div className="ml-3">
              <p className="text-sm font-semibold text-gray-900">All</p>
              <p className="text-xs text-gray-500">{maxFileCount} file{maxFileCount !== 1 ? 's' : ''}</p>
            </div>
          </label>

          {/* Individual document files */}
          {documents.map((doc, idx) => (
            <label
              key={doc.id || idx}
              className={`flex items-start py-3 border-b border-gray-100 pl-4 ${individualDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                checked={selectedDocs.includes(idx)}
                onChange={() => toggleDocFile(idx)}
                disabled={individualDisabled}
                className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-5 w-5 disabled:opacity-50"
              />
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{doc.title}</p>
                <p className="text-xs text-gray-500">1 PDF</p>
              </div>
            </label>
          ))}

          {/* Certificate of Completion */}
          <label className={`flex items-start py-3 pl-4 ${individualDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={downloadCert}
              onChange={() => !individualDisabled && setDownloadCert(!downloadCert)}
              disabled={individualDisabled}
              className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-5 w-5 disabled:opacity-50"
            />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">Certificate of Completion</p>
              <p className="text-xs text-gray-500">1 PDF</p>
            </div>
          </label>
        </div>

        {/* Combine option */}
        <div className="px-6 py-4 border-t border-gray-200">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={combineAll}
              onChange={handleCombineToggle}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 h-5 w-5"
            />
            <span className="ml-3 text-sm text-gray-700">
              Combine all PDFs into a single file
            </span>
          </label>
          {combineAll && (
            <p className="ml-8 mt-1 text-xs text-gray-400">Cover page → signed documents → certificate details</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex justify-between items-center border-t border-gray-100">
          <p className="text-xs text-gray-400">
            {combineAll
              ? '1 combined PDF'
              : totalFileCount > 1
                ? `${totalFileCount} files (ZIP)`
                : totalFileCount === 1
                  ? '1 file (PDF)'
                  : 'No files selected'}
          </p>
          <button
            onClick={handleDownload}
            disabled={totalFileCount === 0 || downloading}
            className="px-6 py-2.5 bg-teal-700 text-white rounded-lg hover:bg-teal-800 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium inline-flex items-center"
          >
            {downloading && <Loader className="h-4 w-4 mr-2 animate-spin" />}
            {downloading ? 'Downloading...' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadFilesModal;
